import { ChangeEvent, FormEvent, KeyboardEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useContextSelector } from 'use-context-selector';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import useProductPriceDrop from '@hooks/product/use-product-price-drop';
import logger from '@helpers/utils/logger/client';
import { debounce } from '@helpers/utils/general';
import { PriceDropContext } from '@context/price-drop-context';
import { PriceDropZonesEnum } from '@enums/price-drop/price-zones';
import styles from './price-drop-form.module.scss';

export const usePriceDropFormPresenter = (productType: 'B2C' | 'C2C') => {
  const { t } = useTranslation('catalog-management');
  const onDropPrice = useContextSelector(PriceDropContext, (a) => a.onDropPrice);
  const updateSellerPrice = useContextSelector(PriceDropContext, (a) => a.updateSellerPrice);
  const updateDebouncedSellerPrice = useContextSelector(PriceDropContext, (a) => a.updateDebouncedSellerPrice);
  const userId = useContextSelector(PriceDropContext, (a) => a.userId);
  const showBuyerFees = useContextSelector(PriceDropContext, (a) => a.showBuyerFees);

  const selectedProductB2C = useContextSelector(PriceDropContext, (a) =>
    productType === 'B2C' ? a.selectedProductB2C : null,
  );
  const selectedProductC2C = useContextSelector(PriceDropContext, (a) =>
    productType === 'C2C' ? a.selectedProductC2C : null,
  );

  const recommendedPrice = useContextSelector(PriceDropContext, (a) => a.recommendedPrice);
  const debouncedSellerPrice = useContextSelector(PriceDropContext, (a) => a.debouncedSellerPrice);
  const isPriceRecommendationLoading = useContextSelector(PriceDropContext, (a) => a.isPriceRecommendationLoading);
  const sellerPrice = useContextSelector(PriceDropContext, (a) => a.sellerPrice);
  const error = useContextSelector(PriceDropContext, (a) => a.error);
  const setError = useContextSelector(PriceDropContext, (a) => a.setError);
  const priceZone = useContextSelector(PriceDropContext, (a) => a.priceZone);
  const isPriceZoneLoading = useContextSelector(PriceDropContext, (a) => a.isPriceZoneLoading);
  const priceDetails = useContextSelector(PriceDropContext, (a) => a.priceDetails);
  const isPriceDetailsLoading = useContextSelector(PriceDropContext, (a) => a.isPriceDetailsLoading);
  const { sendAnalyticEvent } = useAnalyticEvents('my_listings');
  const [inputTouched, setInputTouched] = useState(false);

  const maxPriceC2C = selectedProductC2C?.priceDropped
    ? selectedProductC2C.priceDropped.cents
    : selectedProductC2C?.originalPrice.cents;

  const productPrice =
    productType === 'C2C' && selectedProductC2C
      ? Math.round(maxPriceC2C / 100)
      : Math.round(selectedProductB2C.pvpCents / 100);

  const productIdToUse = productType === 'C2C' ? selectedProductC2C?.id : selectedProductB2C?.id;
  const { useDropPrice } = useProductPriceDrop(userId, productIdToUse);

  const { mutate: dropThePrice, isLoading: isDropPriceLoading } = useDropPrice({
    onSuccess: () => {
      onDropPrice?.(priceDetails);
      sendAnalyticEvent('mifs_price_drop_success', {
        value: String(debouncedSellerPrice),
      });
    },
    onError: (e) => {
      logger.error(error, 'Error while droping the price. Server responded with : ');
      setError(e?.response?.data?.errors?.detail);
    },
  });

  const debouncedSetPrice = useCallback(
    debounce((value: number) => {
      updateDebouncedSellerPrice(value);
    }, 300),
    [],
  );

  const onChangePrice = (e: ChangeEvent<HTMLInputElement>) => {
    const newSellerPrice = String(e.target?.value || '');
    setInputTouched(true);
    updateSellerPrice(Number(newSellerPrice));
    debouncedSetPrice(Number(newSellerPrice));
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Backspace', 'Tab', 'Meta', 'Control'].includes(e.key)) {
      return true;
    }

    if (!/^[0-9]+$/.test(e.key)) {
      e.preventDefault();
      return false;
    }

    return true;
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    dropThePrice(debouncedSellerPrice);
  };

  useEffect(() => {
    let errorMessage = '';
    if (debouncedSellerPrice < priceDetails?.pvpMin) {
      errorMessage = t('SELL.CATALOG_MANAGEMENT.REMOVE_ERROR', {
        minimumprice: `${priceDetails?.pvpMinWithSymbol}`,
      });
    } else if (debouncedSellerPrice >= productPrice) {
      errorMessage = t('SELL.CATALOG_MANAGEMENT.PRICE_DROP_ERROR');
    }

    setError(errorMessage);
  }, [debouncedSellerPrice, priceDetails?.pvpMin, productPrice]);

  useEffect(() => {
    if (!inputTouched && recommendedPrice && sellerPrice === 0 && debouncedSellerPrice === 0) {
      updateSellerPrice(recommendedPrice);
      updateDebouncedSellerPrice(recommendedPrice);
    }
  }, [inputTouched, recommendedPrice, sellerPrice, debouncedSellerPrice]);

  const priceZoneColor = useMemo(() => {
    const zone = priceZone?.id ?? '';
    if (zone === PriceDropZonesEnum.CONVERSION_VERY_OVERPRICED) {
      return 'danger';
    }
    if (zone === PriceDropZonesEnum.CONVERSION_OVERPRICED) {
      return 'warning';
    }
    if (zone === PriceDropZonesEnum.CONVERSION_WELL_PRICED || zone === PriceDropZonesEnum.CONVERSION_AT_PRICE) {
      return 'success';
    }
    if (zone === PriceDropZonesEnum.CONVERSION_UNDERPRICED) {
      return 'underpriced';
    }
  }, [priceZone]);

  const priceInputClassNames = debouncedSellerPrice > 0 &&
    !isPriceZoneLoading && {
      [styles['priceInput--danger']]: error || priceZoneColor === 'danger',
      [styles['priceInput--warning']]: !error && priceZoneColor === 'warning',
      [styles['priceInput--success']]: !error && priceZoneColor === 'success',
      [styles['priceInput--underpriced']]: !error && priceZoneColor === 'underpriced',
    };

  const isRightSymbol = selectedProductC2C
    ? selectedProductC2C.originalPrice.currency !== 'USD'
    : selectedProductB2C.currency !== 'USD';

  return {
    onSubmit,
    onChangePrice,
    onKeyDown,
    sellerFees: priceDetails?.feeDetails,
    sellerEarning: priceDetails?.pvn_without_symbol ?? 0,
    productPrice,
    buyerFeesPrice: priceDetails?.service_fees?.formatted ?? '',
    priceInputClassNames,
    isBuyerFeesVisible: showBuyerFees && !error && priceDetails?.service_fees?.cents > 0,
    isSpinnerVisible: isPriceDetailsLoading || isDropPriceLoading || isPriceRecommendationLoading,
    isSubmitButtonDisabled: isDropPriceLoading || Boolean(error) || isPriceDetailsLoading || !debouncedSellerPrice,
    isRightSymbol: isRightSymbol,
    debouncedSellerPrice,
  };
};
